import { twMerge } from "tailwind-merge"
import { ReactComponent as SendIcon } from "../../../../assets/icons/send.svg";
import { useEffect, useRef, useState } from "react";
import { api } from "../../../../services/api";
import { useQuery } from "@tanstack/react-query";
import { getInitial } from "../../../../services/utils";
import { useUser } from "../../../../services/user-context";
import dayjs from "dayjs";
import { Loading } from "../../../../components/Loading/Loading";
import { LottieLoading } from "../../../../components/Loading/LottieLoading";

function CommentEditor({ onReply }: { onReply: (comment: any) => Promise<void> }) {
    const [comment, setComment] = useState("");
    const { userInfo } = useUser() as any;
    const [saving, setSaving] = useState(false);

    const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        try {
            if (!comment.trim()) {
                return;
            }
            setSaving(true);
            await onReply({ comment });
            setComment("");
            setSaving(false);
        } catch (error) {
            setSaving(false);
        }
        return false;
    }
    return (
        <form className="flex items-center gap-2" onSubmit={handleSubmit}>
            <div className="bg-gray-500 text-[#52525B] text-[clamp(10px,1.5vw,12px)] font-medium leading-[11px] w-6 h-6 rounded-full flex items-center justify-center">{getInitial(userInfo.firstName, userInfo.lastName)}</div>
            <input className="w-full bg-transparent text-xs border-0 outline-none flex-1" placeholder="Add your comment" value={comment} onChange={(e) => setComment(e.target.value)} />
            <button type="submit" disabled={!comment.trim() || saving}>
                {saving ? <Loading /> : <SendIcon className="w-5 h-5 [&_path]:stroke-black" />}
            </button>
        </form>
    )
}

function CommentView({ comment }: { comment: any }) {
    const userName = comment.authorName.split(" ");
    return (
        <div className="flex flex-col gap-1 max-w-[80%]">
            <div className="flex items-start gap-2">
                <div className="bg-gray-500 text-[#52525B] text-[clamp(10px,1.5vw,12px)] font-medium leading-[11px] w-[25px] h-[25px] rounded-full flex items-center justify-center">{getInitial(userName[0], userName[1])}</div>
                <div className="flex-1 text-xs p-2 bg-gray-100 rounded-[5px] text-[clamp(10px,1.5vw,12px)] text-black">{comment.content}</div>
            </div>
            <div className="text-xs text-text-gray-500 text-right text-[clamp(6px,1.5vw,9px)]">{dayjs(comment.createdAt).format("MMMM DD, YYYY hh:mma")}</div>
        </div>
    )
}

export function CertificationComments({ className, canComment = true, assignmentId }: { className?: string, canComment?: boolean, assignmentId: string }) {
    const commentListRef = useRef<HTMLDivElement>(null);
    const { data: commentsData, isLoading: isLoadingComments, refetch: refetchComments } = useQuery({
        queryKey: ["certification-comments", assignmentId],
        queryFn: () => api.getCertificationComments(assignmentId),
        enabled: !!assignmentId,
    });

    const handleSaveComment = async (comment: any) => {
        await api.saveCertificationComment(assignmentId, { content: comment.comment });
        refetchComments();
    }

    useEffect(() => {
        commentListRef.current?.scrollTo({ top: commentListRef.current?.scrollHeight, behavior: "smooth" });
    }, [commentsData]);

    return (
        <div className={twMerge("border border-lightGray rounded-[10px] flex-1 h-full flex flex-col", className)}>
            <div
                className={`flex flex-col gap-2 px-2 py-2.5 text-[#464646] font-semibold text-[clamp(10px,1.5vw,12px)] tracking-[0.3px] uppercase justify-center bg-[#F4F4F5] rounded-t-[10px] border-b border-lightGray`} >
                Comments
            </div>
            {isLoadingComments ? (
                <LottieLoading />
            ) : (
                <>
                    <div className="flex-1 overflow-y-auto h-[calc(100%-150px)] p-2.5 flex flex-col gap-2.5" ref={commentListRef}>

                        {commentsData?.data?.data?.map((comment: any, index: number) => (
                            <CommentView key={index} comment={comment} />
                        ))}
                    </div>
                    {canComment && <div className="bg-[#F4F4F5] p-2 w-full rounded-b-[10px]">
                        <CommentEditor onReply={(comment) => handleSaveComment(comment)} />
                    </div>}
                </>
            )}
        </div>
    )
}